import type { AdminMonitoringPanel, AdminMonitoringStatus } from '@/types/api'

export const MONITORING_INTERVAL_STORAGE_KEY = 'legalprime:monitoring-interval-seconds'

export const MONITORING_INTERVAL_OPTIONS = [10, 15, 30, 60] as const

export type MonitoringIntervalSeconds = (typeof MONITORING_INTERVAL_OPTIONS)[number]

export const DEFAULT_MONITORING_INTERVAL_SECONDS: MonitoringIntervalSeconds = 15

const PANEL_STATUSES: AdminMonitoringStatus[] = ['ok', 'degraded', 'critical']

export function parseMonitoringInterval(value: string | null): MonitoringIntervalSeconds {
  const parsed = Number(value)
  if (MONITORING_INTERVAL_OPTIONS.includes(parsed as MonitoringIntervalSeconds)) {
    return parsed as MonitoringIntervalSeconds
  }

  return DEFAULT_MONITORING_INTERVAL_SECONDS
}

export function readStoredMonitoringInterval(): MonitoringIntervalSeconds {
  if (typeof window === 'undefined') return DEFAULT_MONITORING_INTERVAL_SECONDS
  return parseMonitoringInterval(window.localStorage.getItem(MONITORING_INTERVAL_STORAGE_KEY))
}

export function persistMonitoringInterval(seconds: MonitoringIntervalSeconds): void {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(MONITORING_INTERVAL_STORAGE_KEY, String(seconds))
}

export function isAdminMonitoringPanel(value: unknown): value is AdminMonitoringPanel {
  if (typeof value !== 'object' || value === null) return false
  const panel = value as Partial<AdminMonitoringPanel>
  return (
    typeof panel.status === 'string' &&
    PANEL_STATUSES.includes(panel.status) &&
    Array.isArray(panel.probes) &&
    Array.isArray(panel.history) &&
    typeof panel.payments === 'object' &&
    panel.payments !== null
  )
}

export function resolveAdminMonitoringPanel(value: unknown): AdminMonitoringPanel | null {
  if (isAdminMonitoringPanel(value)) return value
  if (typeof value !== 'object' || value === null || !('data' in value)) return null
  const inner = (value as { data: unknown }).data
  return isAdminMonitoringPanel(inner) ? inner : null
}

export function monitoringStatusLabel(status: AdminMonitoringStatus): string {
  if (status === 'ok') return 'Funcionando'
  if (status === 'degraded') return 'Funcionando com alerta'
  return 'Não está funcionando'
}

export function monitoringStatusBadge(status: AdminMonitoringStatus): string {
  if (status === 'ok') return 'OK'
  if (status === 'degraded') return 'Alerta'
  return 'Falha'
}

export function monitoringGroupLabel(group: string): string {
  if (group === 'infra') return 'Infra'
  if (group === 'widget_sale') return 'Widget e venda'
  if (group === 'payment') return 'Pagamento'
  if (group === 'auth') return 'Autenticação'
  return group
}

export function formatMonitoringHttp(status: number | null): string {
  return status == null ? '—' : String(status)
}

export function formatMonitoringLatency(ms: number | null): string {
  return ms == null ? '—' : `${ms} ms`
}

export function countMonitoringProbes(probes: { status: AdminMonitoringStatus }[]): {
  ok: number
  degraded: number
  critical: number
} {
  return probes.reduce(
    (acc, probe) => {
      acc[probe.status] += 1
      return acc
    },
    { ok: 0, degraded: 0, critical: 0 }
  )
}
